import { router, Stack, useLocalSearchParams, usePathname } from 'expo-router';
import posthog from 'posthog-js';
import { PostHogProvider, usePostHog, useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'react-native-gesture-handler';
import * as Sentry from '@sentry/react-native';
import { AuthProvider, useAuth } from '../src/context/AuthContext.js'; // Import the new provider
import ErrorBoundary from '../src/components/ErrorBoundary.js';
import { OnboardingProvider } from '../src/context/OnboardingContext.js';
import { TierProvider, useTier } from '../src/context/TierContext.js';
import { NegotiationProvider } from '../src/context/NegotiationContext.js';
import env from '../src/utils/environment.js';
import { useFonts } from '../src/utils/fontLoader.js';
import logger from '../src/utils/logger.js';
import { logNavigation } from '../src/utils/sentryBreadcrumbs.js';
import { trackReturnVisitDay } from '../src/utils/gtmHelpers.js';
import { initializeAttribution, initializeBrowserAttribution } from '../src/utils/posthogAttribution.js';
import { filterSentryEvent } from '../src/utils/sentryFilters.js';
import { useOAuthErrorHandler } from '../src/hooks/useOAuthErrorHandler.js';
import { initAuth } from '../src/utils/supabaseClient.js';
import initSupabase from '../src/utils/supabaseInit.js';
import PastDueBanner from '../src/components/common/PastDueBanner.js';
import DesktopProFloatingCard from '../src/components/common/DesktopProFloatingCard.js';
import DesktopDormantModelsCard from '../src/components/common/DesktopDormantModelsCard.js';
import SurveyFloatingCard from '../src/components/survey/SurveyFloatingCard.js';
import ProActivationToast from '../src/components/common/ProActivationToast.js';
import TrialAhaToast from '../src/components/common/TrialAhaToast.js';
import { MODAL_SOURCE_ALLOWLIST } from '../src/components/paywall/upgradeCopy.js';
import TrialUnlockModal from '../src/components/paywall/TrialUnlockModal.js';

// Import release version for Sentry error tracking
// This file is generated by scripts/generate-release.js during build
let releaseInfo: { name: string } | null = null;
try {
  releaseInfo = require('../release.json');
} catch (error) {
  logger.warn('[Sentry] release.json not found. Errors will not be tagged with release version.');
}

const isBrowser = typeof window !== 'undefined';

const useScreenTracking = (isLoading: boolean) => {
  // <-- Accept isLoading state
  const posthog = usePostHog();
  const pathname = usePathname();
  const params = useLocalSearchParams();

  // Track last pageview to prevent duplicates from params/state changes
  const lastPageviewRef = useRef<string | null>(null);

  useEffect(() => {
    // --- Rule: Do not track the screen view until the auth state is loaded ---
    if (isLoading) {
      return;
    }

    if (posthog && pathname) {
      // Include query parameters in the URL for proper attribution tracking (gclid, utm_*, etc.)
      const fullUrl = isBrowser ? window.location.href : pathname;

      // Create unique key from pathname + params to detect true page changes
      const pageKey = `${pathname}|${JSON.stringify(params)}`;

      // Only fire pageview if page actually changed (prevent duplicates from re-renders)
      if (lastPageviewRef.current !== pageKey) {
        const prevPath = lastPageviewRef.current?.split('|')[0] || null;
        logNavigation(prevPath, pathname);
        // Set browser tab title globally
        if (isBrowser) document.title = 'Doocars';
        const eventData = {
          $current_url: fullUrl,
          path: pathname,
          ...params,
        };
        posthog.capture('$pageview', eventData);
        lastPageviewRef.current = pageKey;
      }
    }

    // --- Return a cleanup function to capture the $pageleave event ---
    return () => {
      if (posthog && pathname) {
        // Include query parameters in the URL for proper attribution tracking
        const fullUrl = isBrowser ? window.location.href : pathname;
        // Capture a $pageleave event when the user navigates away from the screen.
        // This is crucial for calculating accurate session durations and bounce rates.
        posthog.capture('$pageleave', {
          $current_url: fullUrl,
          path: pathname,
          ...params,
        });
      }
    };
  }, [posthog, pathname, params, isLoading]);
};

function AppContent() {
  const { user, isLoading, hasCompletedOnboarding, hasActiveFeed, hasSelectedCars, hasFitScores, hasPostcode, onboardingIntent, region } = useAuth();
  const pathname = usePathname();
  const posthog = usePostHog();
  const authHandledRef = React.useRef(false); // Add a ref to track if auth redirection has been handled
  const previousUserRef = React.useRef(user); // Track previous user state to detect logout
  const { tier, subscriptionStatus, realityCheckShownAt, dormancyConfirmedAt, hasProAccess, loading: tierLoading, upgradeModal, dismissUpgradeModal, triggerUpgradeModal, trialUnlockEligible } = useTier();
  useScreenTracking(isLoading);
  // Step 8 (two-phase trial): `trialUnlockEligible` (resolved once in TierProvider
  // from the R8.5 status gate) decides whether an in-flight trial_active user
  // gets the deferred-trial card-entry surface (TrialUnlockModal) INSTEAD of the
  // /pricing instant-pay redirect. Post-trial / ineligible users fall through to
  // the existing path. The two-phase trial is the default + only flow (no
  // experiment).
  const [trialUnlockSource, setTrialUnlockSource] = useState<string | null>(null);
  // Item #3: the tapped locked listing's own below-market delta (£), carried
  // from the upgrade-intent bus into the deferred-trial card-entry modal.
  const [trialUnlockDelta, setTrialUnlockDelta] = useState<number | null>(null);
  // Detect Supabase OAuth error redirects on any route (Supabase's redirectTo
  // can be `/`, `/onboarding`, or `/auth-callback` depending on the flow).
  // Captures to PostHog + Sentry, then routes the user to /onboarding with a
  // user-readable error param. _layout's init useEffects below skip the
  // landing-page defer when error params are present so the Sentry capture
  // actually transports instead of no-op'ing pre-init.
  useOAuthErrorHandler();
  const sessionReplayEnabled = useFeatureFlagEnabled('SessionReplay_Flag');

  // Initialize attribution tracking once on mount (web only)
  useEffect(() => {
    if (!posthog || !isBrowser) {
      return;
    }

    // Track attribution parameters from URL
    // This captures Google Ads parameters (gad_source, gad_campaignid) and other
    // custom parameters that PostHog doesn't automatically capture
    initializeAttribution(posthog);
  }, [posthog]);

  // Track unique visit days for return_visit_3rd_day conversion
  // Waits for auth to resolve so we can pass the user's email for enhanced conversions
  useEffect(() => {
    if (isBrowser && !isLoading) {
      trackReturnVisitDay(user?.email || null);
    }
  }, [isLoading]); // Run once after auth resolves

  useEffect(() => {
    // Feature flag loaded - sessionReplayEnabled variable available for use
    if (sessionReplayEnabled) {
      // Session replay is enabled - can be used for conditional logic
    }
  }, [sessionReplayEnabled]);

  // Step 5: Email/grandfather deep-link consumer
  // Reads ?upgrade_source= and ?coupon= from URL, validates against allowlist,
  // opens the upgrade modal exactly once, then strips both params (preserving UTM/etc).
  // useRef sentinel prevents re-firing on subsequent dep changes (hasProAccess flips
  // when the webhook lands and Realtime updates the tier).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (!user || isLoading || tierLoading) return;
    if (!isBrowser) return;
    if (deepLinkHandledRef.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const upgradeSource = params.get('upgrade_source');
      const coupon = params.get('coupon');
      if (!upgradeSource && !coupon) return;

      // Mark handled BEFORE doing work so any error doesn't cause a retry storm.
      deepLinkHandledRef.current = true;

      const sourceIsAllowed = upgradeSource && MODAL_SOURCE_ALLOWLIST.includes(upgradeSource);
      // LB-5 / D-002: drop !hasProAccess guard so email CTAs open UpgradeModal for Pro
      // users too (e.g. grandfather email while user already has access — modal self-dismisses
      // via Effect 2 in UpgradeModal once hasProAccess is confirmed).
      const shouldOpen = sourceIsAllowed && env.isPaywallEnabled;

      if (shouldOpen) {
        triggerUpgradeModal({
          source: upgradeSource as string,
          coupon: coupon || undefined,
        });
      }

      // Strip BOTH upgrade_source AND coupon unconditionally — preserves UTM,
      // prevents orphan-coupon persistence across refresh.
      params.delete('upgrade_source');
      params.delete('coupon');
      const remainingParams: Record<string, string> = {};
      params.forEach((v, k) => { remainingParams[k] = v; });
      router.replace({ pathname, params: remainingParams });
    } catch (err) {
      logger.warn('[_layout] Deep-link effect error:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoading, tierLoading, hasProAccess]);

  // Reset authHandledRef when user identity changes (login/logout/switch account)
  // Deliberately does NOT reset on session refresh for the same user — prevents
  // magic link re-click from re-triggering the redirect cascade (the SIGNED_IN
  // event fires again with a new user object reference but the same user ID).
  useEffect(() => {
    const previousId = previousUserRef.current?.id;
    const currentId = user?.id;
    logger.log('[_layout] User state changed:', {
      previousUser: previousId,
      currentUser: currentId,
      identityChanged: previousId !== currentId,
    });
    if (previousId !== currentId) {
      authHandledRef.current = false;
    }
    // Note: previousUserRef is NOT updated here - it's updated after navigation logic runs
  }, [user]);

  useEffect(() => {
    logger.log('[_layout] Navigation useEffect triggered:', {
      isLoading,
      authHandled: authHandledRef.current,
      user: user?.id,
      hasCompletedOnboarding,
      pathname,
      previousUser: previousUserRef.current?.id,
    });

    // Wait until the authentication state is fully loaded and auth redirection hasn't been handled
    // Also wait for onboarding status to be loaded (hasCompletedOnboarding !== null)
    if (isLoading || tierLoading || authHandledRef.current || (user && hasCompletedOnboarding === null) || (user && hasCompletedOnboarding === true && (hasActiveFeed === null || hasSelectedCars === null || hasFitScores === null || hasPostcode === null || onboardingIntent === null))) {
      logger.log('[_layout] Skipping navigation logic - early return');
      return;
    }

    // --- Rule 1: Restrict unauthenticated users from accessing protected routes ---
    if (!user) {
      logger.log('[_layout] No user detected, checking protected routes');
      const protectedRoutes = [
        '/dashboard',
        '/car-detail',
        '/profile',
        '/my-reports',
        '/reports',
        '/processing-recommendations',
        '/home',
        '/favorites',
        '/cars',
        '/settings',
        '/car-select',
        '/loading-listings',
        '/feed-ready',
        '/listing',
        '/pick-models',
        '/welcome-back',
        '/email-opt-in',
        '/negotiations',
        '/negotiation',
        '/negotiation-setup',
        '/paste-a-link',
        '/trial-start',
        '/car-selector',
        // Note: /onboarding is NOT protected - unauthenticated users can access it
      ];
      const isTryingToAccessProtectedRoute = protectedRoutes.some((route) =>
        pathname.startsWith(route)
      );

      logger.log('[_layout] Is on protected route?', isTryingToAccessProtectedRoute);

      if (isTryingToAccessProtectedRoute) {
        // Check if user just logged out (previousUserRef had a user, now user is null)
        const justLoggedOut = previousUserRef.current !== null && user === null;

        logger.log('[_layout] Protected route access detected:', {
          justLoggedOut,
          previousUser: previousUserRef.current?.id,
          currentUser: user,
        });

        if (justLoggedOut) {
          // User just logged out from a protected route - redirect to landing page
          logger.log('[_layout] LOGOUT DETECTED - Redirecting to /');
          router.replace('/');
        } else {
          // Unauthenticated user trying to access protected route - redirect to login
          // Save the original URL so we can redirect back after auth
          if (isBrowser) {
            const returnTo = window.location.pathname + window.location.search;
            sessionStorage.setItem('returnTo', returnTo);
            logger.log('[_layout] Saved returnTo for post-login redirect:', returnTo);
          }
          logger.log('[_layout] UNAUTHORIZED ACCESS - Redirecting to /auth?view=login');
          router.replace('/auth?view=login');
        }
        authHandledRef.current = true; // Mark auth redirection as handled
      } else {
        logger.log('[_layout] Not on protected route, no redirect needed');
      }
    }
    // --- Rule 2: Handle authenticated users ---
    else if (user) {
      // Rule 2a: Check if user hasn't completed onboarding and isn't already on onboarding screen
      // Only redirect from pre-onboarding routes. Never yank users away from post-onboarding
      // screens (car-select, loading-listings, etc.) — they reached those via screen-level
      // navigation and a stale/delayed hasCompletedOnboarding value must not disrupt them.
      const postOnboardingRoutes = ['/car-select', '/loading-listings', '/processing-recommendations', '/home', '/feed-ready', '/pick-models', '/welcome-back', '/email-opt-in', '/car-detail', '/profile', '/settings', '/favorites', '/cars', '/listing', '/my-reports', '/reports', '/trial-start', '/car-selector', '/pricing'];
      const isOnPostOnboardingRoute = postOnboardingRoutes.some(r => pathname.startsWith(r));
      if (hasCompletedOnboarding === false && pathname !== '/onboarding' && !isOnPostOnboardingRoute) {
        logger.log('[AppContent] User has not completed onboarding, redirecting to /onboarding');
        router.replace('/onboarding');
        authHandledRef.current = true;
        return;
      }

      // Rule 2b: Redirect authenticated users with completed onboarding away from auth/landing flow
      // Includes '/' so that after login the user is routed to the correct destination.
      // Only fires once per auth cycle (authHandledRef prevents re-triggering on manual navigation).
      if (hasCompletedOnboarding === true) {
        const authFlowRoutes = ['/auth', '/onboarding', '/'];
        const isOnAuthFlowRoute = authFlowRoutes.includes(pathname);

        if (isOnAuthFlowRoute) {
          // Check for returnTo URL saved during protected route redirect
          if (isBrowser) {
            const returnTo = sessionStorage.getItem('returnTo');
            if (returnTo && returnTo.startsWith('/')) {
              sessionStorage.removeItem('returnTo');
              logger.log('[_layout] Post-login redirect to returnTo:', returnTo);
              router.replace(returnTo);
              authHandledRef.current = true;
              previousUserRef.current = user;
              return;
            }
          }

          // Trial interstitial gate: user completed onboarding, has selected cars, but hasn't activated trial
          // Gated by isPaywallEnabled so the guard is dead code until Step 5 flips the kill switch.
          if (env.isPaywallEnabled && hasSelectedCars === true && subscriptionStatus === 'none') {
            logger.log('[_layout] Trial not activated, redirecting to /trial-start');
            router.replace('/trial-start');
            authHandledRef.current = true;
            return;
          }

          // Trial expiry gate (Step 4, Req 8). Under the 2026-05-26 pivot the
          // /reality-check page was merged into /pricing (TrialRecapPanel renders
          // inline above the plans). The locked-tier route guard below catches
          // any non-allowed navigation and bounces to /pricing, where the recap
          // is stamped on mount via mark_reality_check_shown. We only need to
          // force /car-selector for users who have not yet confirmed dormancy.
          if (env.isPaywallEnabled
              && (subscriptionStatus === 'trial_expired' || subscriptionStatus === 'grandfather_expired')
              && !hasProAccess
              && realityCheckShownAt
              && !dormancyConfirmedAt) {
            logger.log('[_layout] Reality check stamped, routing to /car-selector');
            router.replace('/car-selector');
            authHandledRef.current = true;
            return;
          }

          // 6-way routing with intent + returning user support:
          // 1. Deals user + no selected cars → /pick-models
          // 2. Has fit scores, no postcode, UK → /welcome-back (collect postcode first)
          // 3. Has fit scores, no trial yet → /trial-start (tier cap blocks /car-select otherwise)
          // 4. Has fit scores + trial active but no selected cars → /car-select
          // 5. No fit scores yet (car-finder) → /processing-recommendations
          // 6. Has selected cars + feed ready → /home
          // 7. Has selected cars but no feed yet → /loading-listings
          if (hasSelectedCars !== true) {
            if (onboardingIntent === 'deals') {
              router.replace('/pick-models');
            } else if (hasFitScores === true && hasPostcode === false && region === 'UK') {
              router.replace('/welcome-back');
            } else if (hasFitScores === true && subscriptionStatus === 'none') {
              router.replace('/trial-start');
            } else if (hasFitScores === true) {
              router.replace('/car-select');
            } else {
              router.replace('/processing-recommendations');
            }
          } else if (hasActiveFeed === true) {
            router.replace('/home');
          } else {
            router.replace('/loading-listings');
          }
          authHandledRef.current = true;
        }

        // Guard /pick-models: redirect non-deals users to /processing-recommendations
        if (pathname === '/pick-models' && onboardingIntent !== 'deals') {
          router.replace('/processing-recommendations');
          authHandledRef.current = true;
        }

        // Guard /processing-recommendations for users who already have fit scores or selected cars
        if (pathname === '/processing-recommendations') {
          if (hasSelectedCars === true) {
            if (hasActiveFeed === true) {
              router.replace('/home');
            } else {
              router.replace('/loading-listings');
            }
            authHandledRef.current = true;
          } else if (hasFitScores === true) {
            if (hasPostcode === false && region === 'UK') {
              router.replace('/welcome-back');
            } else if (subscriptionStatus === 'none') {
              router.replace('/trial-start');
            } else {
              router.replace('/car-select');
            }
            authHandledRef.current = true;
          }
        }

        // Guard core app routes: redirect users without selected cars back to setup
        const coreAppRoutes = ['/home', '/favorites', '/cars', '/settings', '/listing'];
        const isOnCoreRoute = coreAppRoutes.some((route) => pathname.startsWith(route));
        if (isOnCoreRoute && hasSelectedCars !== true) {
          if (onboardingIntent === 'deals') {
            router.replace('/pick-models');
          } else if (hasFitScores === true && hasPostcode === false && region === 'UK') {
            router.replace('/welcome-back');
          } else if (hasFitScores === true && subscriptionStatus === 'none') {
            router.replace('/trial-start');
          } else if (hasFitScores === true) {
            router.replace('/car-select');
          } else {
            router.replace('/processing-recommendations');
          }
          authHandledRef.current = true;
        }

        // Guard /car-select for users who already have selected cars and active feed
        if (pathname === '/car-select' && hasSelectedCars === true && hasActiveFeed === true) {
          router.replace('/home');
          authHandledRef.current = true;
        }

        // Guard /car-select for free-tier users: trial must activate first so
        // persist-car-selection accepts up to 5 models (free cap = 1).
        if (pathname === '/car-select' && subscriptionStatus === 'none') {
          logger.log('[_layout] Trial not active, redirecting from /car-select to /trial-start');
          router.replace('/trial-start');
          authHandledRef.current = true;
        }

        // Guard /trial-start: redirect users who already activated trial (always active, not gated by isPaywallEnabled)
        if (pathname === '/trial-start' && subscriptionStatus !== 'none') {
          logger.log('[_layout] Trial already active, redirecting from /trial-start to /home');
          router.replace('/home');
          authHandledRef.current = true;
        }

      }
    }

    // Update previousUserRef AFTER navigation logic has run
    // This ensures we can detect logout on the next render
    previousUserRef.current = user;
  }, [user, isLoading, hasCompletedOnboarding, hasActiveFeed, hasSelectedCars, hasFitScores, hasPostcode, onboardingIntent, region, pathname, subscriptionStatus, realityCheckShownAt, dormancyConfirmedAt, hasProAccess, tierLoading]);

  // Locked-tier route guard (2026-05-26 trial/paywall pivot).
  //
  // Under the "no Free / hard paywall after trial" model, tier='Free' is the
  // terminal locked state. Any locked user attempting to reach a protected
  // surface (feed, listing detail, negotiation, etc.) gets redirected to
  // /pricing with a reason param so the page can render the right "your X
  // ended, upgrade to restore access" copy variant. Brand-new pre-trial
  // users (subscription_status='none') stay routed through onboarding /
  // trial-start flow and are NOT caught here.
  //
  // Allowed even when locked: /pricing itself, /profile + /settings (billing
  // management), legal pages, auth/onboarding, and /car-selector — the only
  // remaining Step 4 expiry-flow surface (TrialRecapPanel moved into /pricing
  // under the 2026-05-26 pivot, so /reality-check no longer exists).
  useEffect(() => {
    if (!env.isPaywallEnabled) return;
    if (isLoading || tierLoading) return;
    if (!user || hasCompletedOnboarding !== true) return;

    const LOCKED_STATUSES = ['trial_expired', 'grandfather_expired', 'cancelled', 'past_due', 'paused'];
    const isLockedTier = tier === 'Free' && LOCKED_STATUSES.includes(subscriptionStatus);
    if (!isLockedTier) return;
    if (hasProAccess) return; // Grace-period users still have access

    const ALLOWED_PREFIXES = [
      '/pricing', '/profile', '/settings', '/legal',
      '/auth', '/onboarding', '/landing',
      '/privacy-policy', '/terms-and-conditions', '/cookie-policy',
      '/car-selector',
    ];
    const isAllowed =
      pathname === '/' ||
      ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
    if (isAllowed) return;

    logger.log('[_layout/locked-guard] locked tier on protected route, routing to /pricing', { pathname, subscriptionStatus });
    try {
      posthog?.capture?.('paywall_redirect_intercept', {
        from_route: pathname,
        reason: subscriptionStatus,
      });
    } catch { /* non-fatal */ }
    router.replace(`/pricing?reason=${subscriptionStatus}`);
  }, [user, isLoading, tierLoading, hasCompletedOnboarding, pathname, tier, subscriptionStatus, hasProAccess, posthog]);

  // Step 4 trial-expiry routing guards (Req 8). Runs INDEPENDENTLY of
  // authHandledRef so that a Realtime flip from trial_active → trial_expired
  // (which happens server-side via pg_cron) re-fires the guards even after
  // auth resolution already marked authHandledRef=true earlier in the session.
  // Nested under env.isPaywallEnabled (Req 8 AC 5 kill switch).
  //
  // 2026-05-26 pivot: /reality-check no longer exists. TrialRecapPanel now
  // renders inside /pricing and stamps reality_check_shown_at on mount. So
  // the only Step 4 forcing left is the dormancy-confirmation guard for
  // /car-selector. The locked-tier route guard above handles the rest of the
  // non-/car-selector navigation by bouncing to /pricing.
  useEffect(() => {
    if (!env.isPaywallEnabled) return;
    if (isLoading || tierLoading) return;
    if (!user || hasCompletedOnboarding !== true) return;

    const isOnExpiryRoute = pathname === '/car-selector';
    const isExpiredOrGfExpired = subscriptionStatus === 'trial_expired' || subscriptionStatus === 'grandfather_expired';

    // Guard A (bypass on upgrade): user upgraded mid-flow in another tab.
    if (isOnExpiryRoute && hasProAccess) {
      logger.log('[_layout/step4] Pro access on /car-selector, routing to /home');
      router.replace('/home');
      return;
    }
    // Guard B (post-confirmation bypass): flow complete, prevent bookmark re-entry.
    if (isOnExpiryRoute && !hasProAccess && realityCheckShownAt && dormancyConfirmedAt) {
      logger.log('[_layout/step4] Expiry flow complete, routing to /home');
      router.replace('/home');
      return;
    }
    // Guard C: reality check shown (panel mounted on /pricing), dormancy not
    // confirmed → /car-selector. realityCheckShownAt is the gate so we do not
    // force /car-selector before the user has had a chance to land on
    // /pricing and see the recap.
    if (isExpiredOrGfExpired && !hasProAccess && realityCheckShownAt && !dormancyConfirmedAt && pathname !== '/car-selector') {
      logger.log('[_layout/step4] Reality check stamped, routing to /car-selector');
      router.replace('/car-selector');
      return;
    }
  }, [user, isLoading, tierLoading, hasCompletedOnboarding, pathname, subscriptionStatus, realityCheckShownAt, dormancyConfirmedAt, hasProAccess]);

  // Upgrade-intent bus -> /pricing route. Every legacy triggerUpgradeModal
  // call site (~20 in the codebase) now lands on the pricing page instead of
  // opening the inline UpgradeModal. We preserve the source (and optional
  // preselected/coupon) as query params so /pricing analytics + auto-select
  // can pick them up. dismissUpgradeModal fires immediately so the bus state
  // clears and the old onDismiss callbacks (cleanup hooks set by callers)
  // still run.
  useEffect(() => {
    if (!upgradeModal) return;
    // Step 8: an in-flight trial_active user gets the deferred-trial card-entry
    // surface instead of the instant-pay /pricing redirect (R8.5 + R19). A
    // trial_expired / Free / grandfather user (incl. one who cancelled a carded
    // trial) is NOT eligible and falls through to the existing redirect, so they
    // only ever see instant-pay (no second deferred trial).
    if (trialUnlockEligible) {
      setTrialUnlockSource(upgradeModal.source || 'trial_unlock_cta');
      setTrialUnlockDelta(
        typeof upgradeModal.belowMarketDelta === 'number' ? upgradeModal.belowMarketDelta : null,
      );
      dismissUpgradeModal();
      return;
    }
    const params = new URLSearchParams();
    if (upgradeModal.source) params.set('source', upgradeModal.source);
    if (upgradeModal.preselectedPricingOption) {
      params.set('preselected', upgradeModal.preselectedPricingOption);
    }
    if (upgradeModal.coupon) params.set('coupon', upgradeModal.coupon);
    const qs = params.toString();
    router.push(qs ? `/pricing?${qs}` : '/pricing');
    dismissUpgradeModal();
  }, [upgradeModal, dismissUpgradeModal, trialUnlockEligible]);

  // Display a loading indicator while the user session is being checked.
  // Also wait for onboarding status to be loaded if user is authenticated
  if (isLoading || (user && hasCompletedOnboarding === null)) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#333" />
        <Text style={styles.loadingText}>Initializing Session...</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <PastDueBanner />
      <Stack
        initialRouteName="index"
        screenOptions={{
          headerShown: false,
          animation: 'none', // Disable animations to prevent flashing
          contentStyle: { backgroundColor: '#040409' },
        }}
      >
        <Stack.Screen name="profile" />
        <Stack.Screen
          name="privacy-policy"
          options={{
            headerShown: true,
            headerTitle: '',
            headerLeft: () => (
              <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                <Text style={styles.backButtonText}>{'< Back'}</Text>
              </TouchableOpacity>
            ),
          }}
        />
        <Stack.Screen
          name="terms-and-conditions"
          options={{
            headerShown: true,
            headerTitle: '',
            headerLeft: () => (
              <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                <Text style={styles.backButtonText}>{'< Back'}</Text>
              </TouchableOpacity>
            ),
          }}
        />
        {/* cookie-policy redirects to privacy-policy#cookies */}
        <Stack.Screen name="cookie-policy" options={{ headerShown: false }} />
      </Stack>
      {/* triggerUpgradeModal calls are redirected to /pricing (effect below). */}
      {/* In-app micro-survey: floating card (desktop) / feedback bubble (mobile)
          on /home + listing detail. On /home desktop it suppresses the Pro card
          (see useHomeSurveyActive) so it sits "in place of" the upgrade card. */}
      <SurveyFloatingCard />
      {/* D-025 / D-018: floating Pro card replaces header CTA + countdown chip on desktop */}
      <DesktopProFloatingCard />
      {/* D-033: floating "Models on hold" card, stacks above DesktopProFloatingCard on /home desktop */}
      <DesktopDormantModelsCard />
      {/* D-027: one-shot welcome toast on Pro activation */}
      <ProActivationToast />
      {/* D-026: mid-trial aha-moment toast */}
      <TrialAhaToast />
      {/* Step 8: deferred-trial card-entry surface (gated by trialUnlockEligible
          from useTier above; ineligible/post-trial users never reach it). */}
      <TrialUnlockModal
        isOpen={!!trialUnlockSource}
        onClose={() => {
          setTrialUnlockSource(null);
          setTrialUnlockDelta(null);
        }}
        source={trialUnlockSource || 'trial_unlock_cta'}
        belowMarketDelta={trialUnlockDelta}
      />
    </View>
  );
}

// PostHog initialization — deferred on landing page to improve LCP
let posthogInitialized = false;

function initPostHog() {
  if (posthogInitialized || typeof window === 'undefined' || !env.posthog.apiKey) return;
  posthogInitialized = true;

  // Construct PostHog API host using reverse proxy to bypass ad blockers
  const apiHost = env.posthog.frontendDomain && env.posthog.proxyPath
    ? `https://${env.posthog.frontendDomain}${env.posthog.proxyPath}`
    : env.posthog.host; // Fallback to direct host if proxy not configured

  posthog.init(env.posthog.apiKey, {
    api_host: apiHost,
    ui_host: env.posthog.host, // Direct PostHog URL for toolbar authentication
    person_profiles: 'always',
    opt_in_site_apps: true, // Enable surveys and other site apps

    // SP-023: opt out by default until CookieYes (loaded via GTM) signals
    // analytics consent. A useEffect below listens for cookieyes_consent_update
    // and calls posthog.opt_in_capturing() / startSessionRecording() when
    // analytics_storage is granted.
    opt_out_capturing_by_default: true,
    disable_session_recording: true,

    // Enable session replay with privacy controls (started after consent)
    session_recording: {
      enabled: true,
      maskAllText: false, // Unmask by default - only mask specific sensitive elements
      maskAllImages: false,
      maskAllInputs: true, // Always mask form inputs (covers all login/signup forms)
      recordCrossOriginIframes: false,
      inlineStylesheet: false, // Reduce mutation rate from CSS changes

      // Mask only specific sensitive elements
      maskTextSelector: 'input, textarea, [data-screen="profile"], .user-answer',

      // Block elements that cause excessive mutations (loading spinners, animations)
      blockSelector: '.ph-no-capture, [data-ph-no-capture="true"], .loading-spinner, .progress-bar, [class*="animate-"]',
    },

    // Auto-capture settings
    autocapture: {
      dom_event_allowlist: ['click', 'submit', 'change'],
      capture_pageview: false, // We handle this manually
      capture_pageleave: false, // We handle this manually
    },

    // Maintain existing proxy config
    loaded: (ph) => {
      if (__DEV__) {
        logger.log('[PostHog] Initialized with session replay enabled');
        // Expose PostHog globally for debugging in dev mode
        window.posthog = ph;
        logger.log('[PostHog] Available in console as window.posthog');
      }
    },
  });
}

export default function RootLayout() {
  const { fontsLoaded, error: fontError } = useFonts();
  const pathname = usePathname(); // Get current route for conditional loading

  // Initialize Sentry SDK BEFORE PostHogProvider
  // Deferred on landing page to improve initial load performance
  useEffect(() => {
    const isLandingPage = pathname === '/' || pathname === '/landing';

    const initSentry = () => {
      try {
        if (env.sentry.dsn) {
          // Determine sample rates based on environment
          const isProduction = !__DEV__;
          const tracesSampleRate = isProduction ? 0.1 : 0;

          // Build integrations array conditionally (ReactNativeTracing only works on native platforms)
          const integrations = [];
          // eslint-disable-next-line import/namespace
          if (!isBrowser && Sentry.ReactNativeTracing) {
            // Only add ReactNativeTracing on native platforms (iOS/Android)
            // eslint-disable-next-line import/namespace
            integrations.push(
              // eslint-disable-next-line import/namespace
              new Sentry.ReactNativeTracing({
                tracingOrigins: ['localhost', /^\//],
                enableUserInteractionTracing: true,
              })
            );
          }

          Sentry.init({
            // Core Configuration
            dsn: env.sentry.dsn,
            environment: env.sentry.environment || (isProduction ? 'production' : 'development'),
            debug: __DEV__, // Enable debug logs in development

            // Release tracking - associates errors with source maps
            // Format: cardoo@2.0.0+abc1234 (version+git-hash)
            release: releaseInfo?.name,

            // Performance Monitoring
            tracesSampleRate,
            enableAutoSessionTracking: true,
            sessionTrackingIntervalMillis: 30000, // 30 seconds

            // Native Crash Handling (only relevant for native platforms)
            enableNative: !isBrowser,
            enableNativeCrashHandling: !isBrowser,

            // Session Replay Configuration (disabled to reduce bundle size)
            // Re-enable when needed for debugging specific issues
            // _experiments: {
            //   replaysSessionSampleRate: 0.05, // 5% of sessions
            //   replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
            // },

            // Integrations (conditional based on platform)
            integrations,

            // Privacy & Filtering
            beforeSend: filterSentryEvent,
            sendDefaultPii: false, // Don't send IP addresses and user agent by default
          });

          logger.log(
            '[Sentry] SDK initialized successfully for',
            isBrowser ? 'web' : 'native',
            releaseInfo ? `(release: ${releaseInfo.name})` : '(no release version)'
          );
        } else {
          logger.warn(
            '[Sentry] DSN not configured. Error tracking and performance monitoring will be disabled. This is optional but recommended for production.'
          );
        }
      } catch (error) {
        logger.error('[Sentry] Failed to initialize SDK:', error);
        // Continue app execution even if Sentry fails to initialize
      }
    };

    // Defer Sentry on landing page to improve initial load performance.
    // EXCEPT when the URL carries OAuth error params — Sentry does not buffer
    // pre-init captureMessage calls, so a deferred init would silently drop
    // the warning emitted by useOAuthErrorHandler. Init synchronously in that
    // case so the capture transports.
    const hasOAuthErrorParams = isBrowser && window.location && (
      (window.location.search || '').includes('error=') ||
      (window.location.hash || '').includes('error=')
    );
    if (isLandingPage && isBrowser && !hasOAuthErrorParams) {
      // Use requestIdleCallback to defer Sentry init until browser is idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => initSentry(), { timeout: 3000 });
      } else {
        // Fallback for Safari - defer by 2 seconds
        setTimeout(initSentry, 2000);
      }
      logger.log('[Sentry] Deferred initialization on landing page');
    } else {
      // Initialize immediately on other pages, or when an OAuth error needs capturing
      initSentry();
    }
  }, [pathname]);

  // Defer PostHog initialization on landing page to improve LCP
  // posthog-js buffers calls before init(), so PostHogProvider works immediately
  useEffect(() => {
    const isLandingPage = pathname === '/' || pathname === '/landing';

    // Same OAuth error bypass as Sentry — even though posthog-js buffers
    // pre-init captures, the consent-gate poll below keeps PostHog in
    // opt_out_capturing for up to 6s on a brand-new visitor (no CookieYes
    // action yet), which silently drops the buffered event. Init immediately
    // when the URL carries error params so the capture lands inside the
    // pre-consent window if possible.
    const hasOAuthErrorParams = isBrowser && window.location && (
      (window.location.search || '').includes('error=') ||
      (window.location.hash || '').includes('error=')
    );
    if (isLandingPage && isBrowser && !hasOAuthErrorParams) {
      // Use requestIdleCallback to defer until browser is idle (same pattern as Sentry)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => initPostHog(), { timeout: 3000 });
      } else {
        // Fallback for Safari - defer by 2 seconds
        setTimeout(initPostHog, 2000);
      }
      logger.log('[PostHog] Deferred initialization on landing page');
    } else {
      // Initialize immediately on other pages, or when an OAuth error needs capturing
      initPostHog();
    }
  }, [pathname]);

  // SP-023: gate PostHog capture + session recording on CookieYes consent.
  // CookieYes is loaded by GTM and exposes window.getCkyConsent() once ready.
  // It dispatches `cookieyes_consent_update` on document when the user acts.
  // Until analytics is granted, PostHog stays in opt-out mode.
  useEffect(() => {
    if (!isBrowser) return;

    const applyConsent = (analyticsGranted: boolean) => {
      try {
        if (analyticsGranted) {
          posthog.opt_in_capturing();
          posthog.startSessionRecording();
        } else {
          posthog.opt_out_capturing();
          posthog.stopSessionRecording();
        }
      } catch (err) {
        logger.warn('[PostHog] applyConsent failed', err);
      }
    };

    const readCkyState = () => {
      const cky = (window as unknown as { getCkyConsent?: () => { isUserActionCompleted?: boolean; categories?: Record<string, boolean> } }).getCkyConsent;
      if (typeof cky !== 'function') return null;
      const state = cky();
      return state?.isUserActionCompleted ? state : null;
    };

    // Poll for CookieYes readiness — banner is GTM-injected so it may not be
    // present on first paint. Stop polling after ~6s.
    let attempts = 0;
    const pollInterval = window.setInterval(() => {
      const state = readCkyState();
      if (state) {
        applyConsent(state.categories?.analytics === true);
        window.clearInterval(pollInterval);
        return;
      }
      if (++attempts > 30) {
        window.clearInterval(pollInterval);
      }
    }, 200);

    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<{ accepted?: string[] }>).detail;
      const accepted = detail?.accepted ?? [];
      applyConsent(accepted.includes('analytics'));
    };
    document.addEventListener('cookieyes_consent_update', onConsent as EventListener);

    return () => {
      window.clearInterval(pollInterval);
      document.removeEventListener('cookieyes_consent_update', onConsent as EventListener);
    };
  }, []);

  // Set dark background on body to prevent white flash between navigations
  useEffect(() => {
    if (!isBrowser) return;
    document.documentElement.style.backgroundColor = '#040409';
    document.body.style.backgroundColor = '#040409';
  }, []);

  // PWA manifest + Apple meta tags (once)
  useEffect(() => {
    if (!isBrowser || document.getElementById('pwa-manifest')) return;

    const head = document.head;

    // Web app manifest
    const manifestLink = document.createElement('link');
    manifestLink.id = 'pwa-manifest';
    manifestLink.rel = 'manifest';
    manifestLink.href = '/manifest.json';
    head.appendChild(manifestLink);

    // Apple meta tags
    const metas: [string, string][] = [
      ['apple-mobile-web-app-capable', 'yes'],
      ['apple-mobile-web-app-status-bar-style', 'black-translucent'],
      ['apple-mobile-web-app-title', 'DooCars'],
    ];
    metas.forEach(([name, content]) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      head.appendChild(meta);
    });

    // Apple touch icon
    const touchIcon = document.createElement('link');
    touchIcon.rel = 'apple-touch-icon';
    touchIcon.href = '/apple-touch-icon.png';
    head.appendChild(touchIcon);

    // Theme color
    const themeColor = document.createElement('meta');
    themeColor.name = 'theme-color';
    themeColor.content = '#0556F7';
    head.appendChild(themeColor);
  }, []);

  useEffect(() => {
    if (isBrowser) {
      try {
        // Initialize dataLayer IMMEDIATELY before any other code
        // This ensures it's available for child components that might call trackGTMEvent
        if (!window.dataLayer) {
          window.dataLayer = [];
          logger.log('[GTM] dataLayer initialized');
        }

        // Initialize Google Tag Manager immediately (before any early returns)
        // Note: Consent Mode v2 and CookieYes are configured directly in GTM container
        if (env.gtmContainerId && !document.getElementById('gtm-script')) {
          const gtmScript = document.createElement('script');
          gtmScript.id = 'gtm-script';
          gtmScript.innerHTML = `
            (function(w,d,s,l,i){
              w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${env.gtmContainerId}');
          `;
          document.head.appendChild(gtmScript);
          logger.log('[GTM] Container script injected:', env.gtmContainerId);
        } else if (!env.gtmContainerId) {
          logger.warn('[GTM] Container ID not configured. GTM will not be loaded.');
        }

        const isLandingPage = window.location.pathname === '/' || window.location.pathname === '/landing';

        // Defer Supabase initialization on landing page to improve initial load
        // Auth is only needed when user clicks login button
        if (isLandingPage) {
          // Defer by 1.5 seconds or until user interaction
          const initAuthAfterDelay = () => {
            initSupabase();
            initAuth();
            logger.log('[Auth] Deferred initialization completed on landing page');
          };

          const timeout = setTimeout(initAuthAfterDelay, 1500);

          // Also initialize on first user interaction (mousemove, click, scroll)
          const handleInteraction = () => {
            clearTimeout(timeout);
            initAuthAfterDelay();
            window.removeEventListener('mousemove', handleInteraction);
            window.removeEventListener('click', handleInteraction);
            window.removeEventListener('scroll', handleInteraction);
          };

          window.addEventListener('mousemove', handleInteraction, { once: true });
          window.addEventListener('click', handleInteraction, { once: true });
          window.addEventListener('scroll', handleInteraction, { once: true });

          return () => {
            clearTimeout(timeout);
            window.removeEventListener('mousemove', handleInteraction);
            window.removeEventListener('click', handleInteraction);
            window.removeEventListener('scroll', handleInteraction);
          };
        } else {
          // Initialize immediately on non-landing pages
          initSupabase();
          initAuth();
        }

        // Note: authInitialized state removed to allow non-blocking render for public pages
        logger.log('[RootLayout] Client-side initialization complete (non-blocking)');
      } catch (error) {
        logger.error('Error in RootLayout client-side initialization:', error);
        // Continue app execution even if initialization fails
      }
    }
  }, []);

  // Handle font loading errors
  useEffect(() => {
    if (fontError) {
      logger.error(
        '[Font Loading] Failed to load custom fonts, using system fallbacks:',
        fontError
      );
      // Optionally report to Sentry with lower severity
      Sentry.captureException?.(fontError, {
        level: 'warning',
        tags: { context: 'font_loading' },
      });
    }
  }, [fontError]);

  // If fonts fail to load, continue with system fonts after a brief delay
  // This prevents the app from being stuck on loading screen
  // Note: authInitialized check removed to allow non-blocking render for public pages
  if (!fontsLoaded && !fontError) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>Loading App Resources...</Text>
      </View>
    );
  }

  // During SSR, render without PostHog to avoid "window is not defined" errors
  if (!isBrowser) {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <AuthProvider>
          <TierProvider>
            <NegotiationProvider>
              <OnboardingProvider>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </OnboardingProvider>
            </NegotiationProvider>
          </TierProvider>
        </AuthProvider>
      </GestureHandlerRootView>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <PostHogProvider client={posthog}>
        <AuthProvider>
          <TierProvider>
            <NegotiationProvider>
              <OnboardingProvider>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </OnboardingProvider>
            </NegotiationProvider>
          </TierProvider>
        </AuthProvider>
      </PostHogProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#040409',
  },
  loadingText: {
    fontSize: 16,
    color: '#999',
    marginTop: 10,
  },
  backButton: {
    marginLeft: 10,
    padding: 10,
  },
  backButtonText: {
    color: '#000000',
    fontSize: 16,
  },
});
